import {
  ClipboardCheck,
  ShieldCheck,
  SlidersHorizontal,
  ArrowLeftRight,
  Boxes,
  Building2,
  FolderTree,
  ClipboardList,
  FileText,
  Gauge,
  KeyRound,
  PackageCheck,
  PackageSearch,
  Ruler,
  Scale,
  ScrollText,
  Tag,
  Target,
  Palette,
  Settings,
  ShoppingCart,
  Stamp,
  Truck,
  Users,
  Wallet,
  Warehouse,
} from 'lucide-react';
import type { ComponentType } from 'react';

/**
 * Navegacion agrupada por dominio (spec 46).
 *
 * El orden refleja el flujo logistico real -requerimiento, compra, recepcion,
 * inventario, salida- y no el orden alfabetico ni el de implementacion. Un
 * operario encuentra antes la opcion que busca si el menu sigue el recorrido
 * mental que ya tiene del proceso.
 *
 * `permission` es el codigo que el backend exige. La UI lo usa solo para
 * ocultar lo que el usuario no puede hacer: la autorizacion real siempre
 * ocurre en el servidor (spec 32).
 */

export interface NavItem {
  readonly label: string;
  readonly to: string;
  readonly icon: ComponentType<{ className?: string }>;
  readonly permission?: string;
  /** Todavia no implementado: se muestra atenuado en lugar de ocultarse. */
  readonly pending?: boolean;
}

export interface NavGroup {
  readonly label: string;
  readonly items: readonly NavItem[];
}

export const NAVIGATION: readonly NavGroup[] = [
  {
    label: 'General',
    items: [
      { label: 'Dashboard', to: '/', icon: Gauge },
      // La bandeja de aprobaciones va arriba y no dentro de Abastecimiento:
      // no pertenece a un modulo -aprueba documentos de varios- y es lo que un
      // jefe de area abre todos los dias.
      {
        label: 'Mis aprobaciones',
        to: '/aprobaciones',
        icon: Stamp,
        permission: 'approval.decide',
      },
    ],
  },
  {
    label: 'Abastecimiento',
    items: [
      {
        label: 'Requerimientos',
        to: '/requerimientos',
        icon: ClipboardList,
        permission: 'purchase_requisition.read',
      },
      {
        label: 'Cotizaciones',
        to: '/cotizaciones',
        icon: Scale,
        // El permiso sembrado es `quotation_request.read`: escrito como
        // `quotation.read` el enlace desaparecia incluso para el
        // superadministrador, porque el filtro de la barra lateral
        // oculta lo que no existe.
        permission: 'quotation_request.read',
      },
      {
        label: 'Ordenes de compra',
        to: '/ordenes-compra',
        icon: ShoppingCart,
        permission: 'purchase_order.read',
      },
    ],
  },
  {
    label: 'Almacen',
    /*
      Dos bloques, y el orden no es casual.

      Primero lo que se CONSULTA -que hay, que paso-, porque son las dos
      pantallas a las que mas veces al dia se entra y no tienen por que
      esperar detras de seis operaciones. Despues lo que se HACE, en el orden
      en que la mercaderia recorre el almacen: entra, se decide lo retenido,
      se mueve, sale, se cuenta y se corrige.

      Calidad estaba al final, detras de los ajustes. Es el paso que sigue a
      una recepcion con cuarentena, y ponerlo a seis sitios de distancia de
      "Recepciones" obliga a buscarlo justo cuando hay prisa.
    */
    items: [
      {
        label: 'Inventario',
        to: '/inventario',
        icon: Boxes,
        permission: 'inventory.read',
      },
      {
        label: 'Kardex',
        to: '/kardex',
        icon: PackageSearch,
        permission: 'inventory_movement.read',
      },
      {
        label: 'Recepciones',
        to: '/recepciones',
        icon: PackageCheck,
        permission: 'goods_receipt.read',
      },
      {
        label: 'Calidad',
        to: '/calidad',
        icon: ShieldCheck,
        permission: 'goods_receipt.quality_check',
      },
      {
        label: 'Transferencias',
        to: '/transferencias',
        icon: ArrowLeftRight,
        permission: 'warehouse_transfer.read',
      },
      {
        label: 'Despachos',
        to: '/despachos',
        icon: Truck,
        permission: 'dispatch.read',
      },
      {
        label: 'Inventario fisico',
        to: '/inventario-fisico',
        icon: ClipboardCheck,
        permission: 'physical_inventory.read',
      },
      {
        label: 'Ajustes',
        to: '/ajustes',
        icon: SlidersHorizontal,
        permission: 'inventory.adjust',
      },
    ],
  },
  {
    label: 'Maestros',
    /*
      Primero el producto y luego lo que lo describe -categoria, marca,
      unidad-, porque asi es como se crea: no se da de alta una marca por
      gusto, se da de alta porque hace falta para un producto. Al final, con
      quien y donde se trabaja.

      Alfabetico dejaria "Almacenes" arriba y "Productos" en el cuarto sitio,
      que es justo el que mas se abre.
    */
    items: [
      { label: 'Productos', to: '/productos', icon: Boxes, permission: 'product.read' },
      {
        label: 'Categorias',
        to: '/categorias',
        icon: FolderTree,
        permission: 'product_category.read',
      },
      { label: 'Marcas', to: '/marcas', icon: Tag, permission: 'brand.read' },
      { label: 'Unidades', to: '/unidades', icon: Ruler, permission: 'unit_of_measure.read' },
      { label: 'Proveedores', to: '/proveedores', icon: Users, permission: 'supplier.read' },
      { label: 'Almacenes', to: '/almacenes', icon: Warehouse, permission: 'warehouse.read' },
    ],
  },
  {
    label: 'Organizacion',
    items: [
      {
        label: 'Departamentos',
        to: '/departamentos',
        icon: Building2,
        permission: 'department.read',
      },
      {
        label: 'Centros de costo',
        to: '/centros-de-costo',
        icon: Wallet,
        permission: 'cost_center.read',
      },
      { label: 'Proyectos', to: '/proyectos', icon: Target, permission: 'project.read' },
    ],
  },
  {
    label: 'Analisis',
    items: [
      {
        // "Reportes" y no "Consultas".
        // Antes esto eran consultas parametrizadas: se elegian criterios y
        // salia una tabla que se exportaba a CSV. Ahora cada una trae sus
        // cifras de cabecera, su grafico y descarga en Excel y PDF, que es lo
        // que distingue un reporte de una consulta.
        label: 'Reportes',
        to: '/reportes',
        icon: FileText,
        permission: 'report.read',
      },
    ],
  },
  {
    label: 'Administracion',
    items: [
      { label: 'Usuarios', to: '/usuarios', icon: Users, permission: 'user.read' },
      { label: 'Roles', to: '/roles', icon: KeyRound, permission: 'role.read' },
      { label: 'Auditoria', to: '/auditoria', icon: ScrollText, permission: 'audit.read' },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { label: 'Estado del sistema', to: '/sistema', icon: Settings },
      { label: 'Sistema de diseño', to: '/diseno', icon: Palette },
    ],
  },
];
